import { URLParams } from 'modules/url-params/state/app-url-params-types'
import { FormAddressValue, OrderAddressSlice } from './address-types'
import { StringSchema, ObjectSchema, AnyObject, object, string } from 'yup'

import { i18n } from 'next-i18next'
// @ts-expect-error i18n-zipcodes is a pure JS library and does not have types
import i18nZipcode from 'i18n-zipcodes'
import { State } from 'country-state-city'
import { FIELD_CHARACTER_LIMITS } from 'utils/constants/misc'

export const DEFAULT_BILLING_DETAILS_ADDRESS_COUNTRY = 'US'

/**
 * Checks if a country has states/provinces/regions that should be validated.
 * This is used across different validation schemas to determine if state field should be required.
 *
 * @param countryCode - The ISO country code (e.g., 'US', 'VI', 'CA')
 * @returns true if the country has states and state field should be required, false otherwise
 */
export const countryHasStates = (countryCode: string): boolean => {
  if (!countryCode) return false

  try {
    const states = State.getStatesOfCountry(countryCode)

    return states && states.length > 0
  } catch (error) {
    // If there's an error fetching states, assume no states to be safe
    console.warn(`Error fetching states for country ${countryCode}:`, error)
    return false
  }
}

export const makeOrderAddressInitialState = (urlParams: URLParams): OrderAddressSlice => ({
  value: {
    country: urlParams.country || DEFAULT_BILLING_DETAILS_ADDRESS_COUNTRY,
    state: urlParams.state || '',
    city: urlParams.city || '',
    zipCode: urlParams.zip || '',
    line1: urlParams.street || '',
    line2: undefined,
  },
})

export const getLocalizedLabels = (country: string) => {
  const t = i18n?.t.bind(i18n) || ((key: string, fallback: string) => fallback)

  switch (country) {
    case 'US':
      return {
        state: t('AddressFields.US.state', 'State'),
        postalCode: t('AddressFields.US.zipCode', 'Zip Code'),
      }

    case 'CA':
      return {
        state: t('AddressFields.CA.state', 'Province'),
        postalCode: t('AddressFields.CA.zipCode', 'Postal Code'),
      }

    default:
      return {
        state: t('AddressFields.default.state', 'Region'),
        postalCode: t('AddressFields.default.zipCode', 'Postal Code'),
      }
  }
}

export const makeOrderAddressValidationSchema = (): ObjectSchema<FormAddressValue> => {
  const t = i18n?.t.bind(i18n) || ((key: string, fallback: string) => fallback)

  const schema = object({
    country: string().required(t('AddressFields.validations.country', 'Enter your country')),
    state: string().when('country', ([country], schema) => {
      if (countryHasStates(country)) {
        return schema.required(t('AddressFields.validations.state', 'Enter a state'))
      }

      // For countries without states, the field is optional
      return schema
    }) as StringSchema<string, AnyObject, undefined, ''>,
    city: string()
      .required(t('AddressFields.validations.city', 'Enter a city'))
      .max(
        FIELD_CHARACTER_LIMITS.CITY,
        t('validation.truncation.city', 'City must be 50 characters or less'),
      ),
    zipCode: string().when('country', ([country], schema) => {
      const isUS = country === 'US'

      const validationKey = isUS
        ? 'AddressFields.validations.zipCode'
        : 'AddressFields.validations.postalCode'

      const fallbackMessage = isUS ? 'Enter a valid zip code' : 'Enter a valid postal code'

      schema = schema.required(t(validationKey, fallbackMessage))

      schema = schema.test('zipCode', t(validationKey, fallbackMessage), (value) => {
        if (!value) return true

        // If the country is not supported by i18n-zipcodes, it will throw an exception.
        // We will not validate the zip code and assume it is valid.
        try {
          return i18nZipcode(country, value)
        } catch (e) {
          return true
        }
      })

      return schema
    }) as StringSchema<string, AnyObject, undefined, ''>,
    addressLine1: string()
      .required(t('AddressFields.validations.address', 'Enter your address'))
      .max(
        FIELD_CHARACTER_LIMITS.ADDRESS_LINE_1,
        t('validation.truncation.addressLine1', 'Address must be 512 characters or less'),
      ),
    addressLine2: string().max(
      FIELD_CHARACTER_LIMITS.ADDRESS_LINE_2,
      t('validation.truncation.addressLine2', 'Address line 2 must be 45 characters or less'),
    ),
  })

  return schema
}
